import { useMemo } from 'react';

import { useActivityLogController_getActivityLog } from '@/services/hooks';
import type { ActivityLogController_getActivityLogQueryParams } from '@/services/types';

import { mapActivityLogToDomain } from './mappers';
import type { ActivityLog, ActivityLogQueryFilters, UseGetActivityLogResult } from './types';

const EMPTY_ACTIVITY_LOG: ActivityLog = {
    locationId: 0,
    entries: [],
    pagination: { page: 1, limit: 20, total: 0, hasMore: false },
};

const toApiQueryParams = (
    filters?: ActivityLogQueryFilters
): ActivityLogController_getActivityLogQueryParams | undefined => {
    if (!filters) return undefined;
    return {
        cageId: filters.cageId,
        from: filters.from?.toISOString(),
        to: filters.to?.toISOString(),
        page: filters.page,
        limit: filters.limit,
    };
};

export const useGetActivityLog = (
    locationId: number,
    filters?: ActivityLogQueryFilters,
    options?: { enabled?: boolean }
): UseGetActivityLogResult => {
    const apiParams = toApiQueryParams(filters);
    const query = useActivityLogController_getActivityLog(locationId, apiParams, {
        enabled: options?.enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        refetchInterval: 60_000,
    });

    const data = useMemo(
        () => (query.data?.data ? mapActivityLogToDomain(query.data.data) : EMPTY_ACTIVITY_LOG),
        [query.data?.data]
    );

    return { ...query, isLoading: query.isPending, data };
};
